// HelpMenu — the "?" help button next to the profile avatar in the
// top bar. Opens a dropdown with:
//   - "Help on this page" → deep-links to docs.trailercast.io/{slug}
//     where slug is provided by the current phase via HelpContext.
//     Falls back to "Read the guide" → docs home when no slug set.
//   - "Ask Winter" → triggers the global Ask Winter pill (caller
//     passes onOpenWinter so we don't need to reach into that state)
//   - "Contact support" → mailto:hello@trailercast.io
//   - "What's new" → docs.trailercast.io/changelog
//
// The Help menu is intentionally tiny + not feature-creeping into a
// notification bell or settings shortcut. It's a single-purpose
// "I need help right now" affordance.
//
// Each phase / detail surface in App.jsx registers its slug via the
// useHelp() hook so the deep-link adapts to context. The slug is a
// simple string like "trailers/reviewing" or "calls#detail" — it
// gets concatenated with DOCS_BASE_URL.

import { createContext, useContext, useEffect, useRef, useState } from 'react';

const DOCS_BASE_URL = process.env.REACT_APP_DOCS_URL || 'https://docs.trailercast.io';

// ─── Context ───────────────────────────────────────────────────────

const HelpContext = createContext({
  slug: null,
  setSlug: () => { /* noop */ },
});

/**
 * Provider. Wrap the app once near the root in App.jsx.
 *
 * Stores the current page's help slug in state so any descendant
 * component (the TopBar's HelpMenu, primarily) can read it.
 */
export function HelpProvider({ children }) {
  const [slug, setSlug] = useState(null);
  return (
    <HelpContext.Provider value={{ slug, setSlug }}>
      {children}
    </HelpContext.Provider>
  );
}

/**
 * Hook for any component that wants to register its help slug.
 *
 * Usage:
 *   const { setSlug } = useHelp();
 *   useEffect(() => {
 *     setSlug('reviewing-trailers');
 *     return () => setSlug(null);
 *   }, [setSlug]);
 */
export function useHelp() {
  return useContext(HelpContext);
}

/**
 * Convenience hook for components that just want to register a slug
 * for as long as they're mounted. Common case.
 */
export function useRegisterHelpSlug(slug) {
  const { setSlug } = useHelp();
  useEffect(() => {
    if (!slug) return undefined;
    setSlug(slug);
    return () => setSlug(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);
}

// ─── Icon ──────────────────────────────────────────────────────────

function HelpIcon({ size = 20 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

// ─── Menu ──────────────────────────────────────────────────────────

/**
 * The dropdown button itself. Place inside the TopBar's rightSlot
 * alongside notifications + profile.
 *
 * Props:
 *   onOpenWinter — () => void. The "Ask Winter" menu item triggers
 *                  this. Caller is responsible for opening the
 *                  global Winter pill / chat panel. Optional —
 *                  if not provided, the menu item is hidden.
 */
const SUPPORT_EMAIL = 'hello@trailercast.io';

export default function HelpMenu({ onOpenWinter }) {
  const { slug } = useHelp();
  const [open, setOpen] = useState(false);
  // Brief "Copied!" feedback when the user clicks Contact support.
  // Resets after 1.2s and closes the menu so the click feels resolved.
  const [supportCopied, setSupportCopied] = useState(false);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  // Copy the support email to the clipboard. mailto: links are
  // unreliable in 2026 — most users have no default mail handler
  // (everyone's on Gmail / Outlook web), so clicking a mailto: link
  // does nothing visible and looks broken. Instead: copy the address,
  // show a "Copied!" confirmation, let the user paste into whatever
  // mail surface they actually use. The email also stays visible in
  // the menu item subtitle so even users on a system that blocks
  // clipboard access can read + manually copy.
  const onContactSupport = async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(SUPPORT_EMAIL);
        setSupportCopied(true);
        setTimeout(() => {
          setSupportCopied(false);
          setOpen(false);
        }, 1200);
      } else {
        // Old browser / non-secure context — fall back to mailto. Won't
        // work on every system but it's the only option without clipboard.
        if (typeof window !== 'undefined') window.location.href = `mailto:${SUPPORT_EMAIL}`;
        setOpen(false);
      }
    } catch {
      // Clipboard API can throw on permission denial. Last resort: show
      // the email in an alert so the user has SOMETHING to copy from.
      if (typeof window !== 'undefined') {
        // eslint-disable-next-line no-alert
        window.prompt('Copy this email address:', SUPPORT_EMAIL);
      }
      setOpen(false);
    }
  };

  // Click-outside + Escape close. Same pattern used elsewhere in the
  // app for kebab menus / popovers.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target) &&
        menuRef.current && !menuRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Build the docs URL. If the slug includes an anchor (#section),
  // it works as-is via simple concatenation.
  const helpUrl = slug
    ? `${DOCS_BASE_URL}/${slug}`
    : DOCS_BASE_URL;
  const helpLabel = slug ? 'Help on this page' : 'Read the guide';

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        title="Help"
        aria-label="Help"
        aria-haspopup="menu"
        aria-expanded={open}
        style={{
          width: 36, height: 36,
          padding: 0,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          background: open ? 'rgba(15, 23, 42, 0.06)' : 'transparent',
          border: 'none', borderRadius: 8,
          color: 'var(--room-text-muted, #64748b)',
          cursor: 'pointer',
          transition: 'background 120ms ease',
        }}
        onMouseEnter={(e) => {
          if (!open) e.currentTarget.style.background = 'rgba(15, 23, 42, 0.04)';
        }}
        onMouseLeave={(e) => {
          if (!open) e.currentTarget.style.background = 'transparent';
        }}
      >
        <HelpIcon />
      </button>

      {open && (
        <div
          ref={menuRef}
          role="menu"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            right: 0,
            minWidth: 240,
            background: 'var(--room-surface, #ffffff)',
            border: '1px solid var(--room-border, #e2e8f0)',
            borderRadius: 10,
            boxShadow: '0 8px 24px rgba(15, 23, 42, 0.12)',
            padding: 6,
            zIndex: 50,
            fontFamily: 'inherit',
          }}
        >
          {/* Help on this page (or fallback to Read the guide) */}
          <a
            href={helpUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              setOpen(false);
              // Plausible event so we can see WHICH surfaces drive
              // help opens — extremely actionable signal for which
              // parts of TrailerCast confuse users most.
              try {
                if (typeof window !== 'undefined' && window.plausible) {
                  window.plausible('Help opened', {
                    props: { slug: slug || 'home' },
                  });
                }
              } catch { /* analytics is best-effort */ }
            }}
            style={menuItemStyle}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--room-border-soft, #f8fafc)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            role="menuitem"
          >
            <span>{helpLabel}</span>
            <ExternalIcon />
          </a>

          {onOpenWinter && (
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                onOpenWinter();
              }}
              style={{ ...menuItemStyle, border: 'none', cursor: 'pointer', width: '100%' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--room-border-soft, #f8fafc)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              role="menuitem"
            >
              <span>Ask Winter</span>
            </button>
          )}

          {/* Contact support — copy email to clipboard rather than
              mailto:, which is unreliable when the user has no default
              mail handler. Email shown inline as a subtitle so the
              address is visible regardless of whether the click
              succeeds. */}
          <button
            type="button"
            onClick={onContactSupport}
            style={{
              ...menuItemStyle,
              border: 'none', cursor: 'pointer', width: '100%',
              flexDirection: 'column', alignItems: 'flex-start', gap: 2,
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--room-border-soft, #f8fafc)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            role="menuitem"
          >
            <span style={{
              color: supportCopied ? '#0891b2' : 'var(--room-text-strong, #0f172a)',
              fontWeight: supportCopied ? 600 : 400,
              transition: 'color 120ms ease',
            }}>
              {supportCopied ? 'Email copied to clipboard' : 'Contact support'}
            </span>
            <span style={{
              fontSize: 11,
              color: 'var(--room-text-muted, #64748b)',
              fontVariantNumeric: 'tabular-nums',
            }}>
              {SUPPORT_EMAIL}
            </span>
          </button>

          <a
            href={`${DOCS_BASE_URL}/release-notes`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setOpen(false)}
            style={menuItemStyle}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--room-border-soft, #f8fafc)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            role="menuitem"
          >
            <span>What's new</span>
            <ExternalIcon />
          </a>

          {/* Subtle footer note showing the current page's slug —
              helps debugging + builds trust that the deep-link
              actually goes somewhere relevant. Hidden for the
              generic fallback case. */}
          {slug && (
            <div style={{
              padding: '8px 12px 4px',
              fontSize: 11, color: 'var(--room-text-subtle, #94a3b8)',
              borderTop: '1px solid var(--room-border-soft, #f1f5f9)', marginTop: 4,
              fontFamily: 'inherit',
            }}>
              Page: <code style={{
                fontFamily: 'ui-monospace, monospace',
                fontSize: 10.5, color: 'var(--room-text-muted, #64748b)',
              }}>{slug}</code>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const menuItemStyle = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  gap: 12,
  padding: '10px 12px',
  textDecoration: 'none',
  background: 'transparent',
  borderRadius: 6,
  fontSize: 13,
  color: 'var(--room-text-strong, #0f172a)',
  fontFamily: 'inherit',
  cursor: 'pointer',
  textAlign: 'left',
};

function ExternalIcon() {
  return (
    <svg
      width="12" height="12"
      viewBox="0 0 24 24"
      fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true"
      style={{ color: 'var(--room-text-subtle, #94a3b8)', flexShrink: 0 }}
    >
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  );
}
